import { useAction, useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { PersonIcon, ShortcutsIcon, WalletIcon } from '@blade/ui-kit'
import { isLoggedInAtom } from '@root/entities/auth'
import { basicModeAtom } from '@root/entities/basic'
import { settingScreenAction } from '@root/features/settings/settings-screen/atoms'
import { useBreakpoint } from '@root/shared'
import { languageSelectionAtom } from '@root/shared/localization'
import { LogoGlass, Gear2Icon, GlobeIcon, Lock2Icon } from '@root/shared/ui-kit'

import { SettingsItem } from './settings-item'

type SettingsMainProps = {}

export const SettingsMain: React.FC<SettingsMainProps> = () => {
  const settingScreen = useAction(settingScreenAction)
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()

  const [isLoggedIn] = useAtom(isLoggedInAtom)
  const [languageSelection] = useAtom(languageSelectionAtom)
  const [tradingMode] = useAtom(basicModeAtom)

  const isSettingsDisabled = !isLoggedIn

  const handleSecurity = () => {
    if (isSettingsDisabled) {
      return
    }
    settingScreen('security')
  }
  const handleAppSettings = () => {
    settingScreen('app-settings')
  }
  const handlePolicies = () => settingScreen('policies')
  const handleLanguage = () => settingScreen('language')
  const handleShortcuts = () => settingScreen('shortcuts')
  const handleAccountAndPrivacy = () => {
    if (isSettingsDisabled) {
      return
    }
    settingScreen('account-and-privacy')
  }
  const handleTradingMode = () => {
    if (isSettingsDisabled) {
      return
    }
    settingScreen('trading-mode')
  }

  return (
    <Root>
      <Items>
        <SettingsItem
          icon={<Lock2Icon size={18} />}
          title={t('settings.security')}
          disabled={isSettingsDisabled}
          onClick={handleSecurity}
        />
        <SettingsItem
          icon={<PersonIcon size={18} />}
          title={t('settings.account-and-privacy')}
          disabled={isSettingsDisabled}
          onClick={handleAccountAndPrivacy}
        />
        {/* The mode is shown on the row rather than only inside, the way the
            language is: it decides which balance every trade spends from, so
            it is worth being able to read without opening anything. It is
            left off until the account has answered rather than guessed. */}
        <SettingsItem
          icon={<WalletIcon size={18} />}
          disabled={isSettingsDisabled}
          title={
            <>
              <Language>{t('settings.trading-mode')}:</Language>
              {tradingMode === 'basic' ? (
                <span> {t('settings.trading-mode.basic')}</span>
              ) : undefined}
              {tradingMode === 'advanced' ? (
                <span> {t('settings.trading-mode.advanced')}</span>
              ) : undefined}
            </>
          }
          onClick={handleTradingMode}
        />
        <SettingsItem
          disabled={false}
          icon={<GlobeIcon size={18} />}
          title={
            <>
              <Language>{t('language')}:</Language>
              <span> {t(`language.${languageSelection.lang}`)}</span>
            </>
          }
          onClick={handleLanguage}
        />
        <SettingsItem
          icon={<Gear2Icon size={18} />}
          title={t('settings.app-settings')}
          onClick={handleAppSettings}
        />
        {breakpoint === 'desktop' ? (
          <SettingsItem
            icon={<ShortcutsIcon size={18} />}
            title={t('shortcuts')}
            onClick={handleShortcuts}
            disabled={false}
          />
        ) : undefined}
        <SettingsItem
          icon={<LogoGlass size={18} />}
          title={t('settings.about-the-app')}
          onClick={handlePolicies}
          disabled={false}
        />
      </Items>
    </Root>
  )
}

const Root = styled.div``

const Items = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);

  padding: 12px;
  gap: 10px;
`

const Language = styled.span`
  color: var(--text-9);
`
